<template>
	<label
		class="tw relative inline-flex select-none text-sm"
		:class="[
			disabled ? 'cursor-not-allowed text-gray-50' : 'cursor-pointer',
			{
				'h-9 items-center justify-center whitespace-nowrap rounded-full border bg-white px-4 has-[input:active]:bg-gray-60':
					isChip,
				'supportsHover:hover:bg-gray-40': isChip && !disabled,
				'border-gray-40 bg-gray-30 text-gray-50': isChip && disabled,
				'border-gray-40': isChip && !isChecked && !isValidationVisible,
				'shadow-inset': isChip && !disabled && isChecked,
				'border-error shadow-inset shadow-error': isChip && isValidationVisible && !disabled && !disableErrorDesign,
				'text-error': !isChip && isValidationVisible && !disableErrorDesign,
			},
		]"
		style="-webkit-tap-highlight-color: transparent"
	>
		<input
			:id="id"
			ref="inputRef"
			type="radio"
			class="appearance-none"
			:class="
				srOnlyInput
					? 'sr-only'
					: {
							'w-0': isChip,
							'relative mt-0.5 size-4 shrink-0 cursor-pointer rounded-full border bg-white text-base outline-none before:absolute before:-left-[5px] before:-top-[5px] before:size-[calc(100%+10px)] before:rounded-full after:absolute after:left-1/2 after:top-1/2 after:size-2 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full focus-visible:before:border-2 focus-visible:before:border-black-primary disabled:cursor-not-allowed':
								!isChip,
							'border-gray-50 checked:after:bg-gray-50': !isChip && disabled,
							'checked:after:bg-black-primary active:before:border-2 active:before:border-black-primary':
								!isChip && !disabled,
							'mr-2': !isChip && hasSlot,
							'border-error checked:after:bg-error active:before:border-error':
								!isChip && isValidationVisible && !disableErrorDesign,
							'border-black-primary': !isChip && !isValidationVisible,
					  }
			"
			:checked="isChecked"
			:disabled="disabled"
			:name="radioGroupName || name"
			:required="(isRadioGroupRequired && !disabled) || required"
			:value="radioValue"
			:[dataTestName]="''"
			@change="handleChange"
		/>

		<slot v-if="!iconLeft" />
		<SvgIcon
			v-if="iconName && isChip"
			:icon-name="iconName"
			class="size-5 shrink-0"
			:class="iconLeft ? 'mr-2' : 'ml-2'"
		/>
		<slot v-if="iconLeft" />
	</label>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, computed, watch, watchEffect, inject, useSlots } from "vue";
import useInputValidation from "../../composables/use-input-validation";
import SvgIcon from "../SvgIcon/SvgIcon.vue";
import type { SpriteSymbol } from "../../types/GeneratedSpriteType";

const props = withDefaults(
	defineProps<{
		checked?: boolean;
		dataTestName?: string;
		disabled?: boolean;
		disableErrorDesign?: boolean;
		id?: string;
		modelValue?: string | null;
		name?: string;
		radioValue: string;
		required?: boolean;
		srOnlyInput?: boolean;

		// chip props ↴
		chip?: boolean;
		iconLeft?: boolean;
		iconName?: SpriteSymbol;
	}>(),
	{ dataTestName: "" }
);

const emit = defineEmits<{
	(e: "update:modelValue", value: string | null): void;
	(e: "change", event: Event): void;
	(e: "input", event: Event): void;
}>();

const inputRef = useTemplateRef("inputRef");
const forcedErrorMessage = inject("forcedErrorMessage", undefined);
const { errorMessage, handleInput, isValidationVisible } = useInputValidation(
	inputRef,
	props.name || "",
	undefined,
	forcedErrorMessage
);

const hasSlot = computed(() => !!useSlots().default);

const checkedRadio = inject("checkedRadio", ref<string | null>(null));
const isChecked = computed(
	() =>
		props.checked || // Forced state
		props.radioValue === props.modelValue || // Model state through prop directly
		checkedRadio.value === props.radioValue // Model state through SharedRadioGroup
);
watch(
	() => checkedRadio.value,
	() => {
		if (checkedRadio.value !== props.radioValue) handleInput();
	}
);

const isRadioGroupChip = inject<boolean>("isRadioGroupChip", false);
const isChip = computed(() => isRadioGroupChip || props.chip);

const radioGroupName = inject<string>("radioGroupName");
const isRadioGroupRequired = inject<boolean>("isRadioGroupRequired", false);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateCheckedRadio = inject("updateCheckedRadio", (_value: string | null) => {});
props.checked && updateCheckedRadio(props.radioValue);
watch(
	() => props.modelValue as string | null,
	(newValue) => {
		updateCheckedRadio(newValue);
		handleInput();
	}
);

const handleChange = (event: Event) => {
	const val = (event.target as HTMLInputElement).value;
	updateCheckedRadio(val);
	emit("update:modelValue", val);
	emit("change", event);
	emit("input", event);
	handleInput();
};

const updateInputValidityState = inject(
	"updateInputValidityState",
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	(_errorMessage: string | undefined, _isValidationVisible: boolean | undefined) => null
);
const updateInputValidityStateForSelectionCard = inject(
	"updateInputValidityStateForSelectionCard",
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	(_isValidationVisible: boolean | undefined) => null
);
watchEffect(() => {
	updateInputValidityState(errorMessage.value, isValidationVisible.value);
	updateInputValidityStateForSelectionCard(isValidationVisible.value);
});
</script>
